import useDialog from '@smart-tv/hooks/use-dialog';
import React, { useCallback } from 'react';
import { usePlayerContext } from '../_contexts/player-context';
import useStream from '../_hooks/use-stream';
import { StreamType } from '@smart-tv/typings/content';
import { atom, useAtom, useAtomValue } from 'jotai';
import { IS_BROWSER } from '@smart-tv/utils/is-browser';

import { isSkippingAtom } from '../player-controls/player-controls-navigation/skip-buttons';
import usePlayer from '../use-player';
import { playerWaitingAtom, descalePlayerAtom, openChannelsAtom } from '../atoms';
import { StreamParams } from '../types';
import { hideControlsAtom } from '../player-controls/player-controls-scrubber/player-controls-scrubber-start-over';
import {
  isBackKey,
  isExitKey,
  isEnterKey,
  isRightKey,
  isLeftKey,
  isUpKey,
  isDownKey,
  isSpaceKey,
  registerRemoteKeys
} from '@smart-tv/utils/remoteKeys';
import closeTvApp from '@smart-tv/utils/closeTvApp';
import { useVodPlayer } from '../vod-wrapper/_hooks/use-vod-player';
import useEventListener from '@smart-tv/hooks/use-event-listener';
import useTogglePlayPause from '../_hooks/use-toggle-play-pause';
import { useFocusable } from '@noriginmedia/norigin-spatial-navigation';
import { PLAYER_FOCUS_KEY_ROOT } from '../player-helpers';

export const playerControlsOpenAtom = atom(false);

const usePlayerContent = (streamParams?: StreamParams) => {
  const { streamType } = useStream();
  const { changeLiveChannel, playerIsClosing } = useVodPlayer();

  const [openControls, setOpenControls] = useAtom(playerControlsOpenAtom);
  const [openChannels, setOpenChannels] = useAtom(openChannelsAtom);

  const [descalePlayer, setDescalePlayer] = useAtom(descalePlayerAtom);

  const { opened: dialogOpen } = useDialog();

  const { setPlayerRootElem, firstTimeLoading, settingsOverlay } = usePlayerContext();

  const { togglePlayPause, isDeactivated, canToggle } = useTogglePlayPause();

  const hideControls = useAtom(hideControlsAtom);

  // Never navigable; only a target so spatial focus can be parked when the controls are closed.
  const { ref: rootRef } = useFocusable({ focusKey: PLAYER_FOCUS_KEY_ROOT, focusable: false });
  const closingFromBackRef = React.useRef(false);

  const isSkipping = useAtomValue(isSkippingAtom);
  const playerWaiting = useAtomValue(playerWaitingAtom);

  const { handlePlayerRoutingOnClose, closeStream } = usePlayer({});

  const handleKeyDown = React.useCallback(
    async (event: React.KeyboardEvent | KeyboardEvent) => {
      event.stopPropagation();

      const isBack = isBackKey(event);
      const isExit = isExitKey(event);
      const isEnter = isEnterKey(event);
      const isRight = isRightKey(event);
      const isLeft = isLeftKey(event);
      const isUp = isUpKey(event);
      const isDown = isDownKey(event);

      if (isBack) {
        if (playerIsClosing || closingFromBackRef.current) return;

        if (!descalePlayer) {
          closingFromBackRef.current = true;
          handlePlayerRoutingOnClose();
        } else {
          setDescalePlayer(false);
        }
      }

      if (isExit) {
        if (streamParams) {
          event.preventDefault();
          await closeStream(streamParams);
          closeTvApp();
        }
      }

      if (
        !playerWaiting &&
        !firstTimeLoading &&
        !openChannels &&
        !openControls &&
        !descalePlayer &&
        (isEnter || isRight || isLeft || isUp || isDown)
      ) {
        setOpenControls(true);
      }

      if (
        !IS_BROWSER &&
        !playerWaiting &&
        !firstTimeLoading &&
        !openControls &&
        !openChannels &&
        !descalePlayer &&
        (isUp || isDown) &&
        streamType === StreamType.LiveStream
      ) {
        setOpenControls(false);
        setOpenChannels(true);
      }
    },
    [
      closeStream,
      descalePlayer,
      firstTimeLoading,
      handlePlayerRoutingOnClose,
      openChannels,
      openControls,
      playerIsClosing,
      playerWaiting,
      setDescalePlayer,
      setOpenChannels,
      setOpenControls,
      streamType,
      streamParams
    ]
  );

  const handleSpacebar = useCallback(
    (event: KeyboardEvent) => {
      if (!IS_BROWSER || !isSpaceKey(event) || event.repeat) return;

      if (
        !canToggle ||
        isDeactivated ||
        playerWaiting ||
        firstTimeLoading ||
        openChannels ||
        descalePlayer ||
        settingsOverlay ||
        dialogOpen
      ) {
        return;
      }

      event.preventDefault();
      togglePlayPause();
    },
    [
      canToggle,
      isDeactivated,
      playerWaiting,
      firstTimeLoading,
      openChannels,
      descalePlayer,
      settingsOverlay,
      dialogOpen,
      togglePlayPause
    ]
  );

  useEventListener('keydown', handleSpacebar, undefined, true);

  const handleCloseControls = React.useCallback(() => {
    setOpenControls(false);
    if (rootRef?.current) {
      setPlayerRootElem(rootRef.current);
    }
    if (!dialogOpen) {
      rootRef?.current?.focus();
    }
  }, [dialogOpen, setOpenControls, setPlayerRootElem, rootRef]);

  const handleMouseMove = React.useCallback(() => {
    if (playerWaiting || firstTimeLoading || openChannels || descalePlayer || settingsOverlay)
      return;
    setOpenControls(true);
  }, [
    playerWaiting,
    firstTimeLoading,
    openChannels,
    descalePlayer,
    settingsOverlay,
    setOpenControls
  ]);

  const handleCloseChannels = React.useCallback(() => {
    setOpenControls(false);
    setOpenChannels(false);

    if (rootRef?.current) {
      setPlayerRootElem(rootRef.current);
    }
    rootRef?.current?.focus();
  }, [setOpenChannels, setOpenControls, setPlayerRootElem, rootRef]);

  const handleChannelChange = React.useCallback(
    (newChannelSid: number) => {
      changeLiveChannel({ sid: newChannelSid });
      handleCloseChannels();
    },
    [changeLiveChannel, handleCloseChannels]
  );

  React.useEffect(() => {
    registerRemoteKeys();
  }, []);

  React.useEffect(() => {
    if (rootRef?.current) {
      setPlayerRootElem(rootRef.current);
    }
    rootRef?.current?.focus();
  }, [rootRef, setPlayerRootElem]);

  const handleClickOutside = React.useCallback(() => {
    if (openChannels) handleCloseChannels();
    else if (descalePlayer) setDescalePlayer(false);
    else if (openControls) handleCloseControls();
  }, [
    openChannels,
    descalePlayer,
    openControls,
    handleCloseChannels,
    setDescalePlayer,
    handleCloseControls
  ]);

  return {
    rootRef,
    openControls,
    openChannels,
    handleKeyDown,
    handleMouseMove,
    handleCloseControls,
    handleCloseChannels,
    handleChannelChange,
    handleClickOutside,
    hideControls,
    isSkipping,
    descalePlayer
  };
};

export default usePlayerContent;
